import type { MaintenanceStatus } from '@research-assistant/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { useServerEvent } from '@/integration/runtime/server-events.js';

// Drop every admin-banner dismiss flag so the next maintenance window
// re-surfaces the banner even in the same tab (devs/admins must always
// notice that maintenance is ON, not silently inherit a prior dismiss).
function clearBannerDismissFlags(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('maintenance-banner-dismissed:'))
        stale.push(key);
    }
    for (const key of stale) sessionStorage.removeItem(key);
  } catch {
    // best-effort
  }
}

/**
 * useMaintenance — picks up the on/off toggle from two channels:
 *   1. **Authoritative push**: `maintenance.changed` SSE event broadcast
 *      by the server when an admin flips the switch — invalidates the
 *      query and the downtime screen toggles within ~100ms across every
 *      connected client.
 *   2. **Polling fallback**: 10s / 60s interval on `/v1/maintenance` so
 *      clients that lost the SSE channel (offline, slept tab) still pick
 *      up the change without a manual reload.
 *
 * The endpoint is public (no auth) and `Cache-Control: no-store`
 * server-side, so the poll is the truthful source.
 *
 * Falls back to `{ enabled: false, ... }` on network error so a temporary
 * blip doesn't lock everyone out behind the downtime screen.
 */
export function useMaintenance(): MaintenanceStatus {
  const qc = useQueryClient();
  const q = useQuery<MaintenanceStatus>({
    queryKey: ['maintenance-status'],
    queryFn: async () => {
      const res = await fetch('/v1/maintenance', { credentials: 'omit' });
      if (!res.ok) {
        return {
          enabled: false,
          title: '',
          message: '',
          allowAdmins: true,
        };
      }
      return (await res.json()) as MaintenanceStatus;
    },
    refetchInterval: (q) => (q.state.data?.enabled ? 10_000 : 60_000),
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });

  useServerEvent(
    'maintenance.changed',
    useCallback(() => {
      void qc.invalidateQueries({ queryKey: ['maintenance-status'] });
    }, [qc]),
  );

  // OFF → ON transition: reset banner dismiss state so admins always
  // see the banner on a fresh maintenance window, regardless of whether
  // title/eta changed.
  const prevEnabledRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const current = q.data?.enabled;
    if (prevEnabledRef.current === false && current === true) {
      clearBannerDismissFlags();
    }
    prevEnabledRef.current = current;
  }, [q.data?.enabled]);

  return (
    q.data ?? {
      enabled: false,
      title: '',
      message: '',
      allowAdmins: true,
    }
  );
}
